/**
 * Project overheads API.
 * Contract: `docs/requirements/project-overheads-srs.md` (§ System design → Backend),
 * implemented by OPS-1607.
 */
import { DEFAULT_PAGE_SIZE } from '@/lib/constants/pagination';
import { ApiErrorCode, ApiResponse, withApiErrorHandler } from '@/modules/common/api/errors';
import { CreatedEntity } from '@/modules/common/types/created-entity';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';
import { axiosInstance } from '@/utils/axios';

import {
  mapOverheadsFilterToRequest,
  mapOverheadsFromResponse,
  mapOverheadsSummaryFromResponse,
} from '../mappers/overhead.mapper';
import {
  CreateProjectOverheadRequest,
  UpdateProjectOverheadRequest,
  UpdateProjectOverheadStatusRequest,
} from '../types/dto/request/create-project-overhead.request';
import {
  ProjectOverheadsSummaryRequest,
  SearchProjectOverheadsRequest,
} from '../types/dto/request/search-project-overheads.request';
import {
  ProjectOverheadResponse,
  ProjectOverheadsSummaryResponse,
} from '../types/dto/response/project-overhead.response';
import { GetOverheadsFilter, OverheadListItem, OverheadsSummary } from '../types/overhead';

export interface GetOverheadsParams {
  cursor?: number | null;
  filters?: GetOverheadsFilter;
  pageSize?: number;
}

/**
 * Searches project overheads with cursor pagination.
 * POST /api/project-overheads/search (policy: OverheadsView)
 */
export const getOverheads = async ({
  cursor,
  filters = {},
  pageSize = DEFAULT_PAGE_SIZE,
}: GetOverheadsParams): Promise<PaginatedResponse<OverheadListItem>> => {
  const endpoint = '/api/project-overheads/search';

  const requestBody: SearchProjectOverheadsRequest = {
    filter: mapOverheadsFilterToRequest(filters),
    pagination: { pageSize, cursor },
  };

  const response = await withApiErrorHandler(endpoint, ApiErrorCode.OVERHEADS_LOAD_FAILED, () =>
    axiosInstance.post<ApiResponse<PaginatedResponse<ProjectOverheadResponse>>>(
      endpoint,
      requestBody
    )
  );

  return new PaginatedResponse({
    items: mapOverheadsFromResponse(response.items),
    pagination: response.pagination,
  });
};

/**
 * Loads summary totals for the current filter selection, converted to the
 * organization's default currency by the backend. Type/status filters are
 * intentionally ignored — the cards always show the full breakdown for the
 * selected period/projects/clients.
 * POST /api/project-overheads/summary (policy: OverheadsView)
 */
export const getOverheadsSummary = async (
  filters: GetOverheadsFilter = {}
): Promise<OverheadsSummary> => {
  const endpoint = '/api/project-overheads/summary';

  const requestBody: ProjectOverheadsSummaryRequest = {
    filter: mapOverheadsFilterToRequest({
      ...filters,
      types: undefined,
      statuses: undefined,
    }),
  };

  const response = await withApiErrorHandler(
    endpoint,
    ApiErrorCode.OVERHEADS_SUMMARY_LOAD_FAILED,
    () => axiosInstance.post<ApiResponse<ProjectOverheadsSummaryResponse>>(endpoint, requestBody)
  );

  return mapOverheadsSummaryFromResponse(response);
};

/**
 * Creates a project overhead. When `recurrence` is set, the backend
 * materializes one occurrence per interval (weekly/monthly/quarterly/yearly)
 * from `date` through `recurrence.endDate` inclusive, all linked by a shared
 * series id.
 * POST /api/project-overheads (policy: OverheadsManage) — returns the id of
 * the first occurrence.
 */
export const createOverhead = async (
  data: CreateProjectOverheadRequest
): Promise<CreatedEntity> => {
  const endpoint = '/api/project-overheads';

  return withApiErrorHandler(endpoint, ApiErrorCode.OVERHEAD_CREATE_FAILED, () =>
    axiosInstance.post<ApiResponse<CreatedEntity>>(endpoint, data)
  );
};

/**
 * Updates a single project overhead occurrence (type, project, and recurrence
 * are immutable).
 * PATCH /api/project-overheads/{id} (policy: OverheadsManage)
 * Rejects with backend code 3201 (OVERHEAD_ALREADY_INVOICED) when the overhead
 * is already on an invoice.
 */
export const updateOverhead = async (
  overheadId: number,
  data: UpdateProjectOverheadRequest
): Promise<void> => {
  const endpoint = `/api/project-overheads/${overheadId}`;

  await withApiErrorHandler(endpoint, ApiErrorCode.OVERHEAD_UPDATE_FAILED, () =>
    axiosInstance.patch<ApiResponse<void>>(endpoint, data)
  );
};

/**
 * Flips a COST overhead between PLANNED and PAID.
 * PATCH /api/project-overheads/{id}/status (policy: OverheadsManage)
 * Rejects with backend code 3202 (OVERHEAD_INVALID_STATUS_TRANSITION) for
 * income overheads or invalid transitions.
 */
export const updateOverheadStatus = async (
  overheadId: number,
  data: UpdateProjectOverheadStatusRequest
): Promise<void> => {
  const endpoint = `/api/project-overheads/${overheadId}/status`;

  await withApiErrorHandler(endpoint, ApiErrorCode.OVERHEAD_UPDATE_FAILED, () =>
    axiosInstance.patch<ApiResponse<void>>(endpoint, data)
  );
};

/**
 * Deletes a single project overhead occurrence.
 * DELETE /api/project-overheads/{id} (policy: OverheadsDelete)
 * Rejects with backend code 3201 (OVERHEAD_ALREADY_INVOICED) when invoiced.
 */
export const deleteOverhead = async (overheadId: number): Promise<void> => {
  const endpoint = `/api/project-overheads/${overheadId}`;

  await withApiErrorHandler(endpoint, ApiErrorCode.OVERHEAD_DELETE_FAILED, () =>
    axiosInstance.delete<ApiResponse<void>>(endpoint)
  );
};
